"use client";

/**
 * AI 可視化専用のチャート UI エントリ。
 *
 * @remarks
 * shared chart tooltip は SA/UA/IW でも使われるため、AI 専用の payload 解釈、
 * 詳細な数値フォーマット、`0` / `null` の表示ルールはこのモジュールに閉じ込める。
 */

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { ChevronDownIcon, InfoIcon } from "lucide-react";
import * as React from "react";
import type { TooltipContentProps, TooltipValueType } from "recharts";
import {
  ChartContainer,
  ChartLegend,
  ChartTooltip,
} from "@/app/[lang]/[workspaceId]/_components/chart/chart";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  formatVizTooltipDiffValue,
  formatVizTooltipMetricValue,
} from "./chart-format";

export { ChartContainer, ChartLegend, ChartTooltip };

type TooltipValue = TooltipValueType;
type TooltipName = string | number;
type BaseTooltipProps = Partial<TooltipContentProps<TooltipValue, TooltipName>>;
type TooltipPayloadItem = NonNullable<BaseTooltipProps["payload"]>[number];
type LegendPayloadItem = {
  color?: string;
  dataKey?: string | number;
  payload?: unknown;
  value?: React.ReactNode;
};
type ChartTooltipValueFormatter = (
  value: unknown,
  key: string,
) => React.ReactNode;

type AiChartTooltipContentProps = BaseTooltipProps &
  React.ComponentProps<"div"> & {
    hideLabel?: boolean;
    indicator?: "line" | "dot" | "dashed";
    nameKey?: string;
    labelKey?: string;
    formatToPercent?: boolean;
    formatToPercentFields?: string[];
    resolveScaledValue?: boolean;
    sort?: "asc" | "desc";
    errorMessage?: string;
    allowedKeys?: string[];
    orderKeys?: string[];
    valueFormatter?: ChartTooltipValueFormatter;
    isGrouped?: boolean;
  };

type AiComparisonChartTooltipContentProps = BaseTooltipProps &
  React.ComponentProps<"div"> & {
    comparisonKeyPrefix?: string;
    diffType?: "absolute" | "percentage";
    formatToPercent?: boolean;
    formatToPercentFields?: string[];
    invertDiffColorFields?: string[];
    resolveScaledValue?: boolean;
    formatBaseDate?: (date: string) => string;
    formatComparisonDate?: (date: string) => string;
    formatValue?: (value: unknown, key: string) => string;
    formatDiff?: (diff: number, key: string) => string;
    comparisonDateKey?: string;
    allowedKeys?: string[];
    orderKeys?: string[];
    isGrouped?: boolean;
    sort?: "asc" | "desc";
  };

function getLegendItemKey(item: LegendPayloadItem): string {
  if (typeof item.dataKey === "string" || typeof item.dataKey === "number") {
    return String(item.dataKey);
  }

  if (typeof item.value === "string" || typeof item.value === "number") {
    return String(item.value);
  }

  return "value";
}

function getLegendItemToggleKey(item: LegendPayloadItem): string {
  const payloadRecord = getTooltipPayloadRecord(item.payload);
  const toggleKey = payloadRecord?.toggleKey;
  if (typeof toggleKey === "string" || typeof toggleKey === "number") {
    return String(toggleKey);
  }

  return getLegendItemKey(item);
}

function getLegendItemLabel(item: LegendPayloadItem): React.ReactNode {
  if (item.value !== null && item.value !== undefined && item.value !== "") {
    return item.value;
  }

  return getLegendItemKey(item);
}

export type AiChartLegendContentProps = React.ComponentProps<"div"> & {
  payload?: LegendPayloadItem[];
  verticalAlign?: "top" | "bottom" | "middle";
  visibleKeySet?: ReadonlySet<string>;
  onToggleItem?: (key: string, checked: boolean) => void;
  maxVisibleItems?: number | null;
  isExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

export type AiChartLegendLayout = {
  className: string;
  height: number;
  collapsedHeight: number;
  heightOffset: number;
  maxVisibleItems: number | null;
  wrapperStyle: React.CSSProperties;
};

/**
 * AI 可視化 legend の折り返し前提レイアウトを返す。
 *
 * @remarks
 * Recharts legend は 1 行前提のままだと長い系列名や多系列でカード幅をはみ出しやすい。
 * content 側の wrap と wrapper 側の高さ予約をセットで揃え、折り返し時も plot を潰さない。
 */
export function resolveAiLegendLayout(args: {
  itemCount: number;
  isMobile: boolean;
  isExpanded?: boolean;
  maxVisibleRows?: number | null;
}): AiChartLegendLayout {
  const maxItemsPerRow = args.isMobile ? 3 : 5;
  const rowHeight = args.isMobile ? 22 : 24;
  const verticalPadding = args.isMobile ? 12 : 10;
  const collapsedRowLimit =
    typeof args.maxVisibleRows === "number" && args.maxVisibleRows > 0
      ? Math.floor(args.maxVisibleRows)
      : null;
  const collapsedCapacity =
    collapsedRowLimit === null ? null : maxItemsPerRow * collapsedRowLimit;
  const isExpandable =
    collapsedCapacity !== null && args.itemCount > collapsedCapacity;
  const collapsedVisibleItems = isExpandable
    ? Math.max(1, collapsedCapacity! - 1)
    : args.itemCount;
  const collapsedRenderedItems = isExpandable
    ? collapsedVisibleItems + 1
    : args.itemCount;
  const expandedRenderedItems = isExpandable
    ? args.itemCount + 1
    : args.itemCount;
  const renderedItems =
    isExpandable && args.isExpanded
      ? expandedRenderedItems
      : collapsedRenderedItems;
  const rowCount = Math.max(1, Math.ceil(renderedItems / maxItemsPerRow));
  const collapsedRowCount = Math.max(
    1,
    Math.ceil(collapsedRenderedItems / maxItemsPerRow),
  );
  const height = rowCount * rowHeight + verticalPadding;
  const collapsedHeight = collapsedRowCount * rowHeight + verticalPadding;

  return {
    className: args.isMobile
      ? "w-full flex-wrap gap-x-2 gap-y-1.5 px-1 text-[11px] leading-tight"
      : "w-full flex-wrap gap-x-4 gap-y-2 px-2",
    height,
    collapsedHeight,
    heightOffset: Math.max(0, height - collapsedHeight),
    maxVisibleItems: collapsedCapacity === null ? null : collapsedVisibleItems,
    wrapperStyle: {
      width: "100%",
      maxWidth: "100%",
    },
  };
}

/**
 * AI 可視化向けの legend presenter。
 *
 * @remarks
 * shared legend は見た目専用のため、single-series でも必ず出すこと、
 * hidden series を残したまま click で visible state を切り替えることは
 * AI 側の wrapper に閉じ込めて既存 chart へ波及させない。
 */
export const AiChartLegendContent = React.forwardRef<
  HTMLDivElement,
  AiChartLegendContentProps
>(
  (
    {
      className,
      payload,
      verticalAlign = "bottom",
      visibleKeySet,
      onToggleItem,
      maxVisibleItems,
      isExpanded = false,
      onExpandedChange,
    },
    ref,
  ) => {
    const { t } = useLingui();
    if (!payload?.length) {
      return null;
    }

    const canExpand =
      typeof onExpandedChange === "function" &&
      typeof maxVisibleItems === "number" &&
      payload.length > maxVisibleItems;
    const renderedPayload =
      canExpand && !isExpanded ? payload.slice(0, maxVisibleItems) : payload;

    return (
      <div
        ref={ref}
        className={cn(
          "flex items-center justify-center",
          verticalAlign === "top" ? "pb-3.5" : "pt-3.5",
          className,
        )}
      >
        {renderedPayload.map((item) => {
          const itemKey = getLegendItemKey(item);
          const toggleKey = getLegendItemToggleKey(item);
          const label = getLegendItemLabel(item);
          const isVisible = visibleKeySet ? visibleKeySet.has(toggleKey) : true;
          const canToggle =
            typeof onToggleItem === "function" && toggleKey.length > 0;

          const content = (
            <>
              <div
                className="h-2 w-2 shrink-0 rounded-[2px] border"
                style={{
                  backgroundColor: isVisible ? item.color : "transparent",
                  borderColor: item.color,
                }}
              />
              <span className={cn(!isVisible && "text-muted-foreground")}>
                {label}
              </span>
            </>
          );

          if (!canToggle) {
            return (
              <div
                key={itemKey}
                className={cn(
                  "flex items-center gap-1.5",
                  !isVisible && "opacity-55",
                )}
              >
                {content}
              </div>
            );
          }

          return (
            <button
              key={itemKey}
              type="button"
              aria-pressed={isVisible}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 transition-colors hover:bg-muted/60 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring",
                !isVisible && "opacity-55",
              )}
              onClick={() => {
                onToggleItem(toggleKey, !isVisible);
              }}
            >
              {content}
            </button>
          );
        })}
        {canExpand ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            aria-expanded={isExpanded}
            className="h-auto gap-1.5 px-1.5 py-0.5 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => {
              onExpandedChange(!isExpanded);
            }}
          >
            <ChevronDownIcon
              className={cn(
                "size-3.5 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
            {isExpanded ? t(msg`Less`) : t(msg`More`)}
          </Button>
        ) : null}
      </div>
    );
  },
);
AiChartLegendContent.displayName = "AiChartLegendContent";

function getTooltipPayloadRecord(
  payload: unknown,
): Record<string, unknown> | undefined {
  if (typeof payload !== "object" || payload === null) {
    return undefined;
  }

  return payload as Record<string, unknown>;
}

function getTooltipRowPayloadRecord(
  payload: unknown,
): Record<string, unknown> | undefined {
  const payloadRecord = getTooltipPayloadRecord(payload);
  if (!payloadRecord) {
    return undefined;
  }

  const nestedPayload = payloadRecord.payload;
  if (typeof nestedPayload !== "object" || nestedPayload === null) {
    return undefined;
  }

  return nestedPayload as Record<string, unknown>;
}

function resolveTooltipPayloadValue(
  payload: unknown,
  key: string | undefined,
): string | number | undefined {
  if (!key) {
    return undefined;
  }

  const payloadRecord = getTooltipPayloadRecord(payload);
  const directValue = payloadRecord?.[key];
  if (typeof directValue === "string" || typeof directValue === "number") {
    return directValue;
  }

  const rowPayload = getTooltipRowPayloadRecord(payload);
  const nestedValue = rowPayload?.[key];
  if (typeof nestedValue === "string" || typeof nestedValue === "number") {
    return nestedValue;
  }

  return undefined;
}

function resolveTooltipItemKey(
  payload: unknown,
  keyProp: string | undefined,
  fallbackValues: unknown[],
): string {
  const payloadValue = resolveTooltipPayloadValue(payload, keyProp);
  if (typeof payloadValue === "string" || typeof payloadValue === "number") {
    return String(payloadValue);
  }

  for (const fallbackValue of fallbackValues) {
    if (
      typeof fallbackValue === "string" ||
      typeof fallbackValue === "number"
    ) {
      return String(fallbackValue);
    }
  }

  return "value";
}

function resolveTooltipIndicatorColor(
  item: TooltipPayloadItem | undefined,
  fallbackColor: string | undefined,
): string | undefined {
  if (!item) {
    return fallbackColor;
  }

  const rowPayload = getTooltipRowPayloadRecord(item);
  const fill = rowPayload?.fill;
  if (typeof fallbackColor === "string" && fallbackColor.length > 0) {
    return fallbackColor;
  }
  if (typeof item.color === "string" && item.color.length > 0) {
    return item.color;
  }
  if (typeof fill === "string" && fill.length > 0) {
    return fill;
  }
  if (typeof item.stroke === "string" && item.stroke.length > 0) {
    return item.stroke;
  }
  return undefined;
}

function resolveTooltipRawValue(
  item: TooltipPayloadItem,
  key: string,
  options: {
    resolveScaledValue: boolean;
    isGrouped: boolean;
  },
): unknown {
  if (!options.resolveScaledValue) {
    return item.value;
  }

  const rowPayload = getTooltipRowPayloadRecord(item) ?? {};

  if (key.endsWith("_scaled")) {
    const baseKey = key.replace(/_scaled$/, "");
    if (baseKey in rowPayload) {
      return rowPayload[baseKey];
    }
    if (!options.isGrouped) {
      const baseOriginalKey = `${baseKey}_original`;
      if (baseOriginalKey in rowPayload) {
        return rowPayload[baseOriginalKey];
      }
    }
  }

  if (!options.isGrouped) {
    const originalKey = `${key}_original`;
    if (originalKey in rowPayload) {
      return rowPayload[originalKey];
    }
  }

  return item.value;
}

function resolveTooltipLabelContent(
  value: unknown,
  labelFormatter: BaseTooltipProps["labelFormatter"],
  payload: BaseTooltipProps["payload"],
  labelClassName?: string,
): React.ReactNode {
  if (labelFormatter) {
    return (
      <div className={cn("font-medium", labelClassName)}>
        {labelFormatter(
          typeof value === "number" ||
            React.isValidElement(value) ||
            value == null
            ? value
            : String(value),
          payload ?? [],
        )}
      </div>
    );
  }

  if (value === null || value === undefined || value === "") {
    return null;
  }

  const labelContent = React.isValidElement(value) ? value : String(value);
  return (
    <div className={cn("font-medium", labelClassName)}>{labelContent}</div>
  );
}

function resolveTooltipRowLabel(item: TooltipPayloadItem, key: string): string {
  if (typeof item.name === "string" || typeof item.name === "number") {
    return String(item.name);
  }
  return key;
}

function normalizeTooltipValueDisplay(value: React.ReactNode): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  return value;
}

export type AiChartTooltipSurfaceProps = React.ComponentProps<"div">;

/**
 * AI chart tooltip の surface quality を統一する shared wrapper。
 *
 * @remarks
 * chart ごとに tooltip の内容は違っても、カードの質感が揺れると別 UI に見えやすい。
 * 半透明 background・soft border・軽い shadow をここへ集約して、
 * scatter / line / bar の presenter が同じ visual language を共有できるようにする。
 */
export const AiChartTooltipSurface = React.forwardRef<
  HTMLDivElement,
  AiChartTooltipSurfaceProps
>(({ className, ...props }, ref) => {
  return (
    <div
      data-slot="ai-chart-tooltip-surface"
      ref={ref}
      className={cn(
        "max-w-[min(22rem,calc(100vw-2rem))] rounded-lg border border-border/45 bg-background/92 px-3 py-2.5 text-xs shadow-md shadow-black/8 dark:bg-background/88 dark:shadow-black/18",
        className,
      )}
      {...props}
    />
  );
});
AiChartTooltipSurface.displayName = "AIChartTooltipSurface";

function resolveIsPercentField(
  key: string,
  options: {
    formatToPercent?: boolean;
    formatToPercentFields?: string[];
  },
): boolean {
  return Boolean(
    options.formatToPercent || options.formatToPercentFields?.includes(key),
  );
}

/**
 * AI 可視化向けの標準 tooltip presenter。
 *
 * @remarks
 * 既存画面では 0/null を間引く shared tooltip が使われているため、AI 側だけは
 * inspect 用として `0` を残し、欠損は `-` として明示する。
 */
const ChartTooltipContent = React.memo(
  React.forwardRef<HTMLDivElement, AiChartTooltipContentProps>(
    (
      {
        active,
        payload,
        className,
        indicator = "dot",
        hideLabel = false,
        label,
        labelFormatter,
        labelClassName,
        formatter,
        color,
        nameKey,
        labelKey,
        formatToPercent = false,
        formatToPercentFields,
        resolveScaledValue = false,
        sort,
        errorMessage,
        allowedKeys = [],
        orderKeys,
        valueFormatter,
        isGrouped = false,
      },
      ref,
    ) => {
      const payloadItems = payload ?? [];

      const tooltipLabel = React.useMemo(() => {
        if (hideLabel || payloadItems.length === 0) {
          return null;
        }

        const [firstItem] = payloadItems;
        const normalizedLabel =
          typeof label === "string" || typeof label === "number"
            ? label
            : undefined;
        const value =
          resolveTooltipPayloadValue(firstItem, labelKey) ?? normalizedLabel;

        return resolveTooltipLabelContent(
          value,
          labelFormatter,
          payload,
          labelClassName,
        );
      }, [
        hideLabel,
        label,
        labelClassName,
        labelFormatter,
        labelKey,
        payload,
        payloadItems,
      ]);

      const orderIndexMap = React.useMemo(() => {
        if (!orderKeys || orderKeys.length === 0) {
          return undefined;
        }
        return new Map(orderKeys.map((key, index) => [key, index]));
      }, [orderKeys]);

      const orderedPayload = React.useMemo(() => {
        const items = [...payloadItems];
        if (orderIndexMap) {
          return items.sort((a, b) => {
            const aKey = resolveTooltipItemKey(a, nameKey, [a.name, a.dataKey]);
            const bKey = resolveTooltipItemKey(b, nameKey, [b.name, b.dataKey]);
            const aIndex = orderIndexMap.get(aKey) ?? Number.POSITIVE_INFINITY;
            const bIndex = orderIndexMap.get(bKey) ?? Number.POSITIVE_INFINITY;
            return aIndex - bIndex;
          });
        }

        return items.sort((a, b) => {
          if (
            sort === "asc" &&
            typeof a.value === "number" &&
            typeof b.value === "number"
          ) {
            return a.value - b.value;
          }
          if (
            sort === "desc" &&
            typeof a.value === "number" &&
            typeof b.value === "number"
          ) {
            return b.value - a.value;
          }
          return 0;
        });
      }, [nameKey, orderIndexMap, payloadItems, sort]);

      const filteredPayload = React.useMemo(
        () =>
          orderedPayload.filter((item) => {
            const key = resolveTooltipItemKey(item, nameKey, [
              item.name,
              item.dataKey,
            ]);
            return allowedKeys.length === 0 || allowedKeys.includes(key);
          }),
        [allowedKeys, nameKey, orderedPayload],
      );

      if (!active || payloadItems.length === 0) {
        return null;
      }

      const nestLabel = payloadItems.length === 1 && indicator !== "dot";

      return (
        <AiChartTooltipSurface
          ref={ref}
          className={cn("grid items-start gap-2 px-2.5 py-2", className)}
        >
          {!nestLabel ? tooltipLabel : null}
          <div className="grid gap-1.5">
            {filteredPayload.map((item, index) => {
              const key = resolveTooltipItemKey(item, nameKey, [
                item.name,
                item.dataKey,
              ]);
              const rawValue = resolveTooltipRawValue(item, key, {
                resolveScaledValue,
                isGrouped,
              });
              const isPercentField = resolveIsPercentField(key, {
                formatToPercent,
                formatToPercentFields,
              });
              const value =
                valueFormatter?.(rawValue, key) ??
                formatVizTooltipMetricValue(rawValue, {
                  isPercent: isPercentField,
                });
              const resolvedValue = normalizeTooltipValueDisplay(value);
              const indicatorColor = resolveTooltipIndicatorColor(item, color);
              const itemLabel = resolveTooltipRowLabel(item, key);

              return (
                <div key={String(item.dataKey ?? item.name ?? index)}>
                  <div className="flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground">
                    {formatter && item.value !== undefined && item.name ? (
                      formatter(
                        item.value,
                        item.name,
                        item,
                        index,
                        item.payload,
                      )
                    ) : (
                      <>
                        <div
                          aria-hidden
                          className={cn(
                            "shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)",
                            {
                              "h-2.5 w-2.5": indicator === "dot",
                              "w-1": indicator === "line",
                              "w-0 border-[1.5px] border-dashed bg-transparent":
                                indicator === "dashed",
                              "my-0.5": nestLabel && indicator === "dashed",
                            },
                          )}
                          style={
                            {
                              "--color-bg": indicatorColor,
                              "--color-border": indicatorColor,
                            } as React.CSSProperties
                          }
                        />
                        <div
                          className={cn(
                            "flex flex-1 justify-between leading-none",
                            nestLabel ? "items-end" : "items-center",
                          )}
                        >
                          <div className="grid gap-1.5">
                            {nestLabel ? tooltipLabel : null}
                            <span>{itemLabel}</span>
                          </div>
                          <span className="ml-4 font-mono font-medium tabular-nums text-foreground">
                            {resolvedValue}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {filteredPayload.length === 0 && errorMessage ? (
              <div className="text-destructive flex max-w-48 items-start gap-1">
                <InfoIcon className="mt-[2px] size-3 shrink-0" />
                {errorMessage}
              </div>
            ) : null}
          </div>
        </AiChartTooltipSurface>
      );
    },
  ),
);
ChartTooltipContent.displayName = "AIChartTooltipContent";

/**
 * AI 可視化向けの比較 tooltip presenter。
 *
 * @remarks
 * shared comparison tooltip は既存画面の省略ルールを含むため、AI 側だけは
 * `0` / `null` をそのまま見せて、途中状態や欠損を追跡できるようにする。
 */
const ComparisonChartTooltipContent = React.memo(
  ({
    active,
    payload,
    className,
    comparisonKeyPrefix = "comparing_",
    diffType = "absolute",
    formatToPercent = false,
    formatToPercentFields = [],
    invertDiffColorFields = [],
    resolveScaledValue = false,
    formatBaseDate,
    formatComparisonDate,
    formatValue,
    formatDiff,
    comparisonDateKey = "comparingDate",
    allowedKeys = [],
    isGrouped = false,
    orderKeys,
    sort,
  }: AiComparisonChartTooltipContentProps) => {
    const displayItems = React.useMemo(() => {
      if (!payload?.length) {
        return [];
      }

      const items: Array<{
        key: string;
        baseValue: unknown;
        comparisonValue: unknown;
        diff: number | null;
        percentageDiff: number | null;
        color: string | undefined;
        label: string;
      }> = [];

      const baseKeys = payload
        .map((item) => item.dataKey)
        .filter(
          (key): key is string =>
            typeof key === "string" &&
            !key.startsWith(comparisonKeyPrefix) &&
            key !== "date" &&
            key !== comparisonDateKey,
        );

      const payloadData = getTooltipRowPayloadRecord(payload[0]) ?? {};
      const comparisonOnlyKeys = Object.keys(payloadData)
        .filter((key) => key.startsWith(comparisonKeyPrefix))
        .map((key) => key.replace(comparisonKeyPrefix, ""))
        .filter((key) => !baseKeys.includes(key))
        .filter((key) => !key.endsWith("_original"));
      const allKeys = [...new Set([...baseKeys, ...comparisonOnlyKeys])];

      for (const baseKey of allKeys) {
        if (allowedKeys.length > 0 && !allowedKeys.includes(baseKey)) {
          continue;
        }

        const baseItem = payload.find((item) => item.dataKey === baseKey);
        const comparisonKey = `${comparisonKeyPrefix}${baseKey}`;

        let baseValue: unknown = baseItem?.value;
        let comparisonValue = payloadData[comparisonKey];

        if (resolveScaledValue && !isGrouped) {
          if (baseKey.endsWith("_scaled")) {
            const originalKey = baseKey.replace(/_scaled$/, "");
            if (baseItem && originalKey in (baseItem.payload ?? {})) {
              baseValue = baseItem.payload?.[originalKey];
            } else if (!baseItem && originalKey in payloadData) {
              baseValue = payloadData[originalKey];
            }

            const comparisonOriginalKeySuffix = `${comparisonKeyPrefix}${originalKey}`;
            if (comparisonOriginalKeySuffix in payloadData) {
              comparisonValue = payloadData[comparisonOriginalKeySuffix];
            }
          }

          const baseOriginalKey = `${baseKey}_original`;
          const comparisonOriginalKey = `${comparisonKey}_original`;
          if (baseOriginalKey in payloadData) {
            baseValue = payloadData[baseOriginalKey];
          }
          if (comparisonOriginalKey in payloadData) {
            comparisonValue = payloadData[comparisonOriginalKey];
          }
        }

        let diff: number | null = null;
        let percentageDiff: number | null = null;
        if (
          typeof baseValue === "number" &&
          typeof comparisonValue === "number"
        ) {
          if (diffType === "absolute") {
            diff = baseValue - comparisonValue;
            if (comparisonValue !== 0) {
              percentageDiff =
                ((baseValue - comparisonValue) / comparisonValue) * 100;
            }
          } else if (comparisonValue !== 0) {
            diff = ((baseValue - comparisonValue) / comparisonValue) * 100;
          }

          const isPercentField = resolveIsPercentField(baseKey, {
            formatToPercent,
            formatToPercentFields,
          });
          if (
            isPercentField &&
            comparisonValue !== 0 &&
            percentageDiff === null
          ) {
            percentageDiff =
              ((baseValue - comparisonValue) / comparisonValue) * 100;
          }
        }

        const comparisonItem = payload.find(
          (item) => item.dataKey === comparisonKey,
        );

        items.push({
          key: baseKey,
          baseValue,
          comparisonValue,
          diff,
          percentageDiff,
          color: resolveTooltipIndicatorColor(
            baseItem ?? comparisonItem,
            undefined,
          ),
          label:
            (baseItem ? resolveTooltipRowLabel(baseItem, baseKey) : baseKey) ||
            baseKey,
        });
      }

      return items;
    }, [
      allowedKeys,
      comparisonDateKey,
      comparisonKeyPrefix,
      diffType,
      formatToPercent,
      formatToPercentFields,
      isGrouped,
      payload,
      resolveScaledValue,
    ]);

    const orderIndexMap = React.useMemo(() => {
      if (!orderKeys || orderKeys.length === 0) {
        return undefined;
      }
      return new Map(orderKeys.map((key, index) => [key, index]));
    }, [orderKeys]);

    const orderedDisplayItems = React.useMemo(() => {
      const items = [...displayItems];
      if (orderIndexMap) {
        return items.sort((a, b) => {
          const aIndex = orderIndexMap.get(a.key) ?? Number.POSITIVE_INFINITY;
          const bIndex = orderIndexMap.get(b.key) ?? Number.POSITIVE_INFINITY;
          return aIndex - bIndex;
        });
      }
      if (!sort) {
        return items;
      }
      return items.sort((a, b) => {
        if (
          typeof a.baseValue === "number" &&
          typeof b.baseValue === "number"
        ) {
          return sort === "asc"
            ? a.baseValue - b.baseValue
            : b.baseValue - a.baseValue;
        }
        return 0;
      });
    }, [displayItems, orderIndexMap, sort]);

    if (!active || !payload || payload.length === 0) {
      return null;
    }

    const payloadData = getTooltipRowPayloadRecord(payload[0]) ?? {};
    const baseDate = payloadData.date;
    const comparisonDate = payloadData[comparisonDateKey];

    const formatValueFn = (value: unknown, key: string): string => {
      if (formatValue) {
        return formatValue(value, key);
      }
      return formatVizTooltipMetricValue(value, {
        isPercent: resolveIsPercentField(key, {
          formatToPercent,
          formatToPercentFields,
        }),
      });
    };

    const formatDiffFn = (diff: number | null, key: string): string => {
      if (diff === null) {
        return "-";
      }
      if (formatDiff) {
        return formatDiff(diff, key);
      }
      return formatVizTooltipDiffValue(diff, {
        diffType,
        isPercent: resolveIsPercentField(key, {
          formatToPercent,
          formatToPercentFields,
        }),
      });
    };

    const resolveDiffColorClassName = (diff: number | null, key: string) => {
      if (diff === null || diff === 0) {
        return "text-muted-foreground";
      }
      if (invertDiffColorFields.includes(key)) {
        return diff > 0
          ? "text-red-600 dark:text-red-400"
          : "text-green-600 dark:text-green-400";
      }
      return diff > 0
        ? "text-green-600 dark:text-green-400"
        : "text-red-600 dark:text-red-400";
    };

    const formatDateValue = (
      value: unknown,
      formatter: ((date: string) => string) | undefined,
    ) => {
      if (value === null || value === undefined) {
        return null;
      }
      const dateValue = typeof value === "string" ? value : String(value);
      return formatter ? formatter(dateValue) : dateValue;
    };

    return (
      <AiChartTooltipSurface
        className={cn("grid items-start gap-3", className)}
      >
        <div className="flex gap-3">
          <div className="flex min-w-[220px] flex-col gap-1.5">
            {baseDate ? (
              <div className="font-medium">
                {formatDateValue(baseDate, formatBaseDate)}
              </div>
            ) : null}

            <div className="grid gap-1.5">
              {orderedDisplayItems.map((item) => (
                <div key={`base-${item.key}`}>
                  <div className="grid grid-cols-[auto_minmax(80px,1fr)_auto_auto] items-center gap-x-2">
                    <div
                      aria-hidden
                      className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: item.color }}
                    />
                    <span className="truncate whitespace-nowrap leading-[1.25]">
                      {item.label}
                    </span>
                    <span
                      className={cn(
                        "min-w-[4em] font-mono text-right text-xs tabular-nums leading-[1.25]",
                        resolveDiffColorClassName(item.diff, item.key),
                      )}
                    >
                      {formatDiffFn(item.diff, item.key)}
                      {item.percentageDiff !== null ? (
                        <span className="ml-1 text-[11px]">
                          ({item.percentageDiff > 0 ? "+" : ""}
                          {item.percentageDiff.toFixed(2)}%)
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={cn(
                        "min-w-[4em] text-right font-mono font-semibold tabular-nums leading-tight text-foreground",
                        (item.baseValue === null ||
                          item.baseValue === undefined) &&
                          "text-muted-foreground",
                      )}
                    >
                      {formatValueFn(item.baseValue, item.key)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {comparisonDate ? (
            <div className="flex min-w-0 w-fit flex-col gap-1 border-l border-border/50 pl-1 pt-0.5">
              <div className="font-medium text-muted-foreground">
                {formatDateValue(comparisonDate, formatComparisonDate)}
              </div>

              <div className="grid gap-1.5">
                {orderedDisplayItems.map((item) => (
                  <div key={`comparison-${item.key}`}>
                    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2">
                      <div
                        aria-hidden
                        className="h-2.5 w-2.5 shrink-0 rounded-[2px] border-[1.5px] border-dashed bg-transparent"
                        style={{ borderColor: item.color }}
                      />
                      <span
                        className={cn(
                          "min-w-0 text-right font-mono font-semibold tabular-nums leading-[1.25] text-muted-foreground",
                          (item.comparisonValue === null ||
                            item.comparisonValue === undefined) &&
                            "text-muted-foreground",
                        )}
                      >
                        {formatValueFn(item.comparisonValue, item.key)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </AiChartTooltipSurface>
    );
  },
);
ComparisonChartTooltipContent.displayName = "AIComparisonChartTooltipContent";

export { ChartTooltipContent, ComparisonChartTooltipContent };
